import { useState } from "react";
import { Link } from "react-router-dom";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";

export interface ProductSection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
  codes?: string[];
}

export interface ProductPageData {
  title: string;
  intro: string;
  sections: ProductSection[];
  enquiry?: string[];
  orderingNote?: string;
  hideColourNote?: boolean;
  hideProcessingNote?: boolean;
  cataloguePdf?: string;
}

const COLOUR_NOTE =
  "Colour photos are for reference only. As natural stone, colour and veining may vary from batch to batch and from the photo or sample shown; samples are for general colour reference only and cannot guarantee an exact match.";

function ProductPageContent({ data }: { data: ProductPageData }) {
  const [showCataloguePreview, setShowCataloguePreview] = useState(false);

  // On a phone, an inline PDF iframe is hard to zoom and page through,
  // so open the catalogue in a new full-screen tab instead.
  const handleCatalogueClick = () => {
    const isMobile =
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 768px), (pointer: coarse)").matches;
    if (isMobile && data.cataloguePdf) {
      // Same tab, so the phone's back button returns to the product page.
      window.location.href = `${data.cataloguePdf}#toolbar=0&navpanes=0`;
      return;
    }
    setShowCataloguePreview(!showCataloguePreview);
  };
  return (
    <Box className="my-10 mx-auto px-4 max-w-5xl">
      <Typography variant="h4" component="h1" className="mb-4">
        {data.title}
      </Typography>
      <Typography variant="body1" className="mb-6">
        {data.intro}
      </Typography>

      {data.cataloguePdf && (
        <Box className="mb-8">
          <Button
            onClick={handleCatalogueClick}
            variant="contained"
            color="brand"
            sx={{
              backgroundColor: "#7c312a",
              fontWeight: 700,
              fontSize: "1.05rem",
              px: 3,
              py: 1.2,
              "&:hover": { backgroundColor: "#5e231d" },
            }}
          >
            {showCataloguePreview ? "Hide catalogue preview" : "View catalogue (PDF)"}
          </Button>
          <Typography
            variant="body2"
            className="mt-2"
            sx={{ color: "#7c312a", fontWeight: 600 }}
          >
            Online preview only — please enquire for a copy of the catalogue.
          </Typography>
          <Typography variant="body2" className="mt-1 block md:hidden">
            On a phone, this opens full-screen. Tap Back to return here.
          </Typography>
          {showCataloguePreview && (
            <Box className="mt-4">
              <iframe
                src={`${data.cataloguePdf}#toolbar=0&navpanes=0`}
                title="Catalogue preview"
                width="100%"
                height="700"
                style={{ border: "1px solid #ccc", borderRadius: "8px" }}
              />
            </Box>
          )}
        </Box>
      )}

      {data.sections.map((section) => (
        <Box key={section.heading} className="mb-8">
          <Typography variant="h6" component="h2" className="mb-2">
            {section.heading}
          </Typography>
          {section.paragraphs?.map((p) => (
            <Typography key={p} variant="body1" className="mb-2">
              {p}
            </Typography>
          ))}
          {section.bullets && (
            <ul className="list-disc ml-6 space-y-1">
              {section.bullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}
          {section.codes && (
            <Stack direction="row" flexWrap="wrap" gap={1} className="mt-2">
              {section.codes.map((c) => (
                <Chip key={c} label={c} size="small" variant="outlined" />
              ))}
            </Stack>
          )}
        </Box>
      ))}

      {!data.hideColourNote && (
      <Box className="mb-8">
        <Typography variant="h6" component="h2" className="mb-2">
          Colour reference
        </Typography>
        <Typography variant="body1">{COLOUR_NOTE}</Typography>
        <Typography variant="body1" className="mt-2">
          Tell us the colour you want. We can send several similar colours for
          you to choose from, and help you find the colour and finish you
          need.
        </Typography>
      </Box>
      )}

      {!data.hideProcessingNote && (
      <Box className="mb-8">
        <Typography variant="h6" component="h2" className="mb-2">
          Finishes and processing
        </Typography>
        <Typography variant="body1">
          We process to meet clients&apos; finish requirements, cut to size.
          See our shared{" "}
          <Link to="/products/processing" className="underline">
            Processing
          </Link>{" "}
          page for the full finishes list. If you need a finish not listed
          there, send us a photo and we will have the factory process it to
          match.
        </Typography>
      </Box>
      )}

      <Box className="mb-8">
        <Typography variant="h6" component="h2" className="mb-2">
          Ordering
        </Typography>
        <ul className="list-disc ml-6 space-y-1">
          <li>Preferred minimum order: one full container, FCL 20&apos; container.</li>
          <li>
            LCL (consolidated) shipment can be discussed if the customer
            agrees to the additional cost.
          </li>
          <li>
            Price on request: price depends on colour, stone type, processing,
            size, quantity, and destination.
          </li>
          <li>We process and deliver to meet our clients&apos; requirements.</li>
          {data.orderingNote && <li>{data.orderingNote}</li>}
        </ul>
      </Box>

      {data.enquiry && (
        <Box className="mb-8">
          <Typography variant="h6" component="h2" className="mb-2">
            What to include in your enquiry
          </Typography>
          <ul className="list-disc ml-6 space-y-1">
            {data.enquiry.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </Box>
      )}

      <Stack direction="row" spacing={2}>
        <Button component={Link} to="/contact" variant="contained" color="brand">
          Request Quote
        </Button>
        <Button
          component="a"
          href="mailto:info@mingstone.com"
          variant="outlined"
          color="brand"
        >
          Email Us
        </Button>
      </Stack>
      <Typography variant="body2" className="mt-4">
        Email: info@mingstone.com, mingstonesupplier@gmail.com,
        mingstone-yantai@163.com. Phone: +1 (604) 791-7855. Goods are shipped
        directly from China or other origins to your designated location.
      </Typography>
    </Box>
  );
}

export default ProductPageContent;
